import { useEffect, useState } from 'react';
import { SESSION_VERIFICATION_RETRY_DELAY_MS } from 'common/constants/auth/session';
import {
  getStoredAccessToken,
  getStoredRefreshToken,
  isAuthLogoutInProgress,
} from 'pages/Auth/utils/authStorage';
import { singleFlightTokenRefresh } from 'pages/Auth/utils/tokenRefresh';

// A stored refresh token with no access token is a restorable session regardless of the stay-signed-in
// preference: sessionStorage survives a reload, so an opted-out tab is entitled to the same recovery.
const shouldRestoreRememberedSession = () =>
  !getStoredAccessToken() && Boolean(getStoredRefreshToken()) && !isAuthLogoutInProgress();

const useRestoreRememberedSession = () => {
  const [isRestoringRememberedSession, setIsRestoringRememberedSession] = useState(shouldRestoreRememberedSession);

  useEffect(() => {
    let isMounted = true;
    let retryTimeout: ReturnType<typeof setTimeout> | undefined;

    // Every exit routes through here
    const finishRestoring = () => {
      if (isMounted) {
        setIsRestoringRememberedSession(false);
      }
    };

    // A remembered refresh token can outlive the access token. Restore it before sign-in redirect.
    const attemptRestore = async (isRetry: boolean) => {
      if (!shouldRestoreRememberedSession()) {
        finishRestoring();
        return;
      }

      const result = await singleFlightTokenRefresh();

      if (!isMounted) {
        return;
      }

      // 'transient' means the refresh token is still valid and still stored — a network issue or 5xx.
      // Retry once, then stop without clearing anything so the next load can still recover the session.
      if (result.kind === 'transient' && !isRetry) {
        retryTimeout = setTimeout(() => {
          attemptRestore(true);
        }, SESSION_VERIFICATION_RETRY_DELAY_MS);
        return;
      }

      finishRestoring();
    };

    attemptRestore(false);

    return () => {
      isMounted = false;
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, []);

  return {
    // Read through on every render rather than latching the restored token in state
    accessToken: getStoredAccessToken(),
    isRestoringRememberedSession,
  };
};

export default useRestoreRememberedSession;
